'use client';

import { useState, useEffect } from 'react';
import { AnchorHTMLAttributes, CSSProperties } from 'react';
import { Icon } from '../icons';
import ReactTooltip from 'react-tooltip';
import React from 'react';
import getInitials from '../../lib/helpers/getInitials';
import { PreferredAppearances } from '../../lib/helpers';
import { Tooltip } from '../Tooltip';
import styles from './ProfileImage.module.scss';

type AvatarProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
	imageURL?: string | null;
	width?: number | string;
	borderIfName?: boolean;
};

type AvatarSubject = {
	firstName?: string | null;
	lastName?: string | null;
	firstNameOne?: string | null;
	lastNameOne?: string | null;
	name?: string | null;
	image?: string | null;
	updatedAt?: string | Date | null;
};

type Sponsor = {
	sponsorLink?: string;
	logo?: string | null;
	name?: string;
	description?: string;
};

export type ProfileImageProps = {
	contact?: AvatarSubject;
	user?: AvatarSubject;
	organization?: AvatarSubject;
	id?: string;
	width?: number | string;
	withIcon?: boolean;
	type?: string;
	sponsor?: Sponsor;
	tooltip?: boolean;
	trackEvent?: () => void;
	preferredAppearance?: ( typeof PreferredAppearances )[keyof typeof PreferredAppearances];
	disablePortal?: boolean;
	dataCy?: string;
	borderIfName?: boolean;
	styles?: Record<string, string | undefined>;
	className?: string;
};

const getAvatarStyle = ( width?: number | string, imageURL?: string | null ) =>
	( {
		'--profile-image-width':
			typeof width === 'number' ? `${ width }px` : width || '75px',
		...( imageURL ? { '--profile-image-url': `url("${ imageURL }")` } : {} ),
	} as CSSProperties );

const Image = ( { imageURL, width, className, ...props }: AvatarProps ) => (
	<a
		className={ `${ styles.image } ${ className || '' }` }
		style={ getAvatarStyle( width, imageURL ) }
		{ ...props }
	/>
);

const Name = ( { width, borderIfName, className, ...props }: AvatarProps ) => (
	<a
		className={ `${ styles.name } ${ borderIfName ? styles.hasBorder : '' } ${
			className || ''
		}` }
		style={ getAvatarStyle( width ) }
		{ ...props }
	/>
);

export const ProfileImage = ( props: ProfileImageProps ) => {
	const componentStyles = props.styles;
	const transformClassNameWithStyles = ( className: string ) =>
		[
			props.className,
			componentStyles
				? className
					.split( ' ' )
					.map( ( n: string ) => componentStyles[ n ] )
					.join( ' ' )
				: className,
		].join( ' ' );

	const [ updatedAt, setUpdatedAt ] = useState<string | Date | null | undefined>(
		null
	);

	useEffect( () => {
		ReactTooltip.rebuild();
		if ( props.user && props.user.updatedAt !== updatedAt ) {
			setUpdatedAt( props.user.updatedAt );
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ props.user ] );

	useEffect( () => {
		if ( props.organization && props.organization.updatedAt !== updatedAt ) {
			setUpdatedAt( props.organization.updatedAt );
		}
	}, [ props.organization, updatedAt ] );

	/**
	 * Get user title
	 */
	const getTitle = ( user?: AvatarSubject ) => {
		let title = '';

		if ( !user ) return title;

		if ( user.firstName || user.firstNameOne ) {
			title += user.firstName || user.firstNameOne;
		}

		if ( user.lastName || user.lastNameOne ) {
			title += ' ';
			title += user.lastName || user.lastNameOne;
		}

		if ( user.name && !title ) {
			title = user.name;
		}

		return title;
	};

	const { id, user, organization, width, withIcon, type, sponsor, contact } =
		props;
	const contactUserImage = user?.image;

	if ( contact ) {
		if (
			contactUserImage &&
			props.preferredAppearance !== PreferredAppearances.initials
		) {
			return (
				<Tooltip
					disablePortal={ props.disablePortal }
					target={
						<Image
							id={ id }
							className={ transformClassNameWithStyles(
								'profile-image profile-image__user'
							) }
							imageURL={ contactUserImage }
							width={ width }
							data-cy={ props.dataCy }
						/>
					}
				>
					{ getTitle( contact ) }
				</Tooltip>
			);
		}

		return (
			<Tooltip
				disablePortal={ props.disablePortal }
				target={
					<Name
						id={ id }
						className={ transformClassNameWithStyles(
							'profile-icon profile-initials  profile-icon__user'
						) }
						width={ width }
						data-cy={ props.dataCy }
						borderIfName={ props.borderIfName }
					>
						{ contact.firstName || contact.lastName ? (
							<span>{ getInitials( contact ) }</span>
						) : (
							<Icon type='profile-placeholder' />
						) }
					</Name>
				}
			>
				{ getTitle( contact ) }
			</Tooltip>
		);
	}

	if ( user ) {
		if (
			user.image &&
			props.preferredAppearance !== PreferredAppearances.initials
		) {
			let imageURL = user.image;
			if ( updatedAt ) imageURL = user.image + '?updatedAt=' + updatedAt;
			return (
				<Tooltip
					disablePortal={ props.disablePortal }
					target={
						<Image
							id={ id }
							className={ transformClassNameWithStyles(
								'profile-image profile-image__user'
							) }
							imageURL={ imageURL }
							width={ width }
							data-cy={ props.dataCy }
						/>
					}
				>
					{ getTitle( user ) }
				</Tooltip>
			);
		}

		return (
			<Tooltip
				disablePortal={ props.disablePortal }
				target={
					<Name
						id={ id }
						className={ transformClassNameWithStyles(
							'profile-initials  profile-icon__user'
						) }
						width={ width }
						data-cy={ props.dataCy }
						borderIfName={ props.borderIfName }
					>
						<span>{ getInitials( user ) }</span>
					</Name>
				}
			>
				{ getTitle( user ) }
			</Tooltip>
		);
	}

	if ( organization ) {
		if (
			organization.image &&
			props.preferredAppearance !== PreferredAppearances.initials
		) {
			const imageURL = `${ organization.image }?updatedAt=${ organization.updatedAt }`;

			return (
				<Tooltip
					disablePortal={ props.disablePortal }
					target={
						<Image
							id={ id }
							className={ transformClassNameWithStyles(
								'profile-image  profile-image__organization'
							) }
							imageURL={ imageURL }
							width={ width }
							data-cy={ props.dataCy }
						/>
					}
				>
					{ getTitle( organization ) }
				</Tooltip>
			);
		}

		return (
			<Tooltip
				disablePortal={ props.disablePortal }
				target={
					<Name
						id={ id }
						className={ transformClassNameWithStyles(
							'profile-icon  profile-icon__organization'
						) }
						width={ width }
						data-cy={ props.dataCy }
					>
						{ props.preferredAppearance !== PreferredAppearances.initials ? (
							<Icon type='organization' />
						) : (
							<span>{ getInitials( organization ) }</span>
						) }
					</Name>
				}
			>
				{ getTitle( organization ) }
			</Tooltip>
		);
	}

	if ( withIcon ) {
		const iconType = type ? type : 'organization';
		return (
			<Tooltip
				disablePortal={ props.disablePortal }
				target={
					<Name
						id={ id }
						className={ transformClassNameWithStyles(
							'profile-icon  profile-icon__' + type
						) }
						width={ width }
						data-cy={ props.dataCy }
						borderIfName={ props.borderIfName }
					>
						<Icon type={ iconType } />
					</Name>
				}
			>
				{ getTitle( organization ) }
			</Tooltip>
		);
	}

	if ( sponsor ) {
		return (
			<div>
				<Image
					id={ id }
					className={ transformClassNameWithStyles(
						'sponsor-image sponsor-image__sponsor'
					) }
					imageURL={ sponsor.logo }
					width={ width }
					data-cy={ props.dataCy }
				/>
			</div>
		);
	}
	return <div />;
};
